import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Linkedin, ChevronLeft, ChevronRight } from 'lucide-react';
import apiService from '../services/api';

const Team = () => {
  const [team, setTeam] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchTeam = async () => {
      const data = await apiService.getTeam();
      setTeam(data);
    };
    fetchTeam();
  }, []);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width >= 1024) setVisibleCount(6);
      else if (width >= 768) setVisibleCount(3);
      else if (width >= 640) setVisibleCount(2);
      else setVisibleCount(1);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  // Drive the grid off the actual member list, so commenting a member in or out
  // of src/mock/mock.js is all that is needed.
  const slides = team;
  const totalSlides = slides.length;

  // Never show more columns than there are members
  const perView = Math.max(1, Math.min(visibleCount, totalSlides || 1));
  const isStatic = perView >= totalSlides;

  // Use the original slides for static layouts, otherwise build an infinite scroll loop
  const displaySlides = isStatic
    ? slides
    : [
        ...slides.slice(-perView),
        ...slides,
        ...slides.slice(0, perView)
      ];

  // Reset to actual position after reaching clone
  useEffect(() => {
    if (!isTransitioning) return;

    const resetTimeout = setTimeout(() => {
      if (currentIndex <= 0) {
        setIsTransitioning(false);
        setCurrentIndex(totalSlides);
      } else if (currentIndex >= totalSlides + perView) {
        setIsTransitioning(false);
        setCurrentIndex(perView);
      } else {
        setIsTransitioning(false);
      }
    }, 500);

    return () => clearTimeout(resetTimeout);
  }, [currentIndex, isTransitioning, totalSlides, perView]);

  // Initialize position
  useEffect(() => {
    setCurrentIndex(perView);
  }, [perView]);

  // Auto-advance carousel - respects pause state
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Don't start new interval if paused or when all slides are visible
    if (isPaused || isStatic) {
      return;
    }

    // Start new interval
    intervalRef.current = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }, 4500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused, isStatic]);

  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleDotClick = (dotIndex) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(dotIndex + perView);
  };

  const handleMouseEnter = () => {
    if (!isManuallyPaused) {
      setIsPaused(true);
    }
  };

  const handleImageClick = () => {
    setIsManuallyPaused((prev) => !prev);
    setIsPaused((prev) => !prev);
  };

  const handleMouseLeave = () => {
    if (!isManuallyPaused) {
      setIsPaused(false);
    }
  };

  // Calculate active dot
  const activeDot = totalSlides
    ? (((currentIndex - perView) % totalSlides) + totalSlides) % totalSlides
    : 0;

  // Calculate transform for carousel behavior; static layout stays at zero
  const translateValue = isStatic ? 0 : (currentIndex * 100) / perView;

  if (totalSlides === 0) return null;

  return (
    <section id="team" className="py-15 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-gray-900 mb-6">
            Our Team
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
Meet the team behind Intellexa, a collective of IIT Madras students bound by a shared passion for technology and a drive to create with purpose. What began as a mutual curiosity has evolved into a team committed to building, innovating, and solving problems that matter.
Blending deep technical expertise with thoughtful creativity, we turn ideas into meaningful, real-world solutions.          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden">
            <div
              className="flex"
              style={{
                transform: `translateX(-${translateValue}%)`,
                transition: isTransitioning ? 'transform 500ms ease-out' : 'none',
                gap: perView === 1 ? '0' : '1rem',
              }}
            >
              {displaySlides.map((member, index) => (
                <div
                  key={`${member.id}-${index}`}
                  className="flex-shrink-0"
                  style={{ 
                    width: perView === 1 
                      ? '100%' 
                      : `calc((100% - ${(perView - 1) * 1}rem) / ${perView})` 
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <Card className="group border-gray-200 hover:border-gray-900 transition-all duration-300 hover:shadow-lg overflow-hidden h-full">
                    <CardContent className="p-0 h-full flex flex-col">
                      {/* Image container with 2px margin */}
                      <div className="relative h-64 overflow-hidden bg-gray-50 p-[2px]">
                        <div className="relative w-full h-full overflow-hidden rounded-sm">
                          <img
                            src={member.image}
                            alt={member.name}
                            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110 cursor-pointer"
                            onClick={handleImageClick}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-10"
                            onClick={(e) => member.linkedin === '#' && e.preventDefault()}
                          >
                            <Linkedin className="w-5 h-5 text-gray-900" />
                          </a>
                        </div>
                      </div>

                      <div className="p-4 flex-1">
                        <h3 className="text-lg font-normal text-gray-900 mb-1">
                          {member.name}
                        </h3>
                        <p className="text-sm text-blue-600 mb-3">{member.role}</p>
                        <p className="text-sm text-gray-600 leading-relaxed line-clamp-4">
                          {member.bio}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {!isStatic && (
            <>
              <button
                type="button"
                onClick={handlePrev}
                disabled={isTransitioning}
                className="absolute left-0 top-1/2 z-10 -translate-y-1/2 -translate-x-1/2 rounded-full bg-white/90 border border-gray-200 shadow-sm p-3 text-gray-800 transition hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={isTransitioning}
                className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2 rounded-full bg-white/90 border border-gray-200 shadow-sm p-3 text-gray-800 transition hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {!isStatic && (
          <div className="mt-6 flex justify-center gap-2">
            {Array.from({ length: totalSlides }).map((_, dotIndex) => (
              <button
                key={dotIndex}
                type="button"
                onClick={() => handleDotClick(dotIndex)}
                disabled={isTransitioning}
                className={`h-2.5 w-2.5 rounded-full transition disabled:cursor-not-allowed ${activeDot === dotIndex ? 'bg-gray-900' : 'bg-gray-300 hover:bg-gray-400'}`}
                aria-label={`Show slide ${dotIndex + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Team;